/**
 * CodeMirror 6 `CompletionSource` for the formula editor: functions, documents, sheets and
 * columns, each inserted with the syntax the grammar actually wants.
 *
 * What the caret sits in decides what is offered, and `completionContextAt` is the single place
 * that decision is made. It reads `tokenizeLenient` rather than scanning for brackets by hand, so
 * it can only fall out of step with the grammar if the tokenizer does — the strict lexer and the
 * lenient one are already kept in lockstep for the same reason.
 *
 * Inserting is done with `apply` as a function rather than a replacement string, because every
 * insertion has to look at what is already there: the user may have typed the quote, the dot or
 * the bracket themselves, and CM6's own `closeBrackets()` may have left a `]` sitting after the
 * caret. Producing `''Hoja''[[` instead of `'Hoja'[` is the failure mode this guards against.
 *
 * `refBeingTyped`/`funcBeingTyped` remain exported as thin wrappers: `FormulaEditor.tsx` uses them
 * for the `insertRequest` gesture and for the "is the user still mid-token" heuristic that keeps
 * the syntax error quiet between keystrokes.
 */

import { startCompletion, type Completion, type CompletionContext, type CompletionResult, type CompletionSource } from '@codemirror/autocomplete'
import type { EditorView } from '@codemirror/view'
import {
  KEYWORD_ARGUMENTS,
  containerNameNeedsQuotes,
  normaliseContainerName,
  type FormulaLocale,
  type FunctionMetadata,
} from '@/shared/formula/language'
import type { CellType } from '../Grid/types'
import { normalizeGridSearchText } from '../Grid/search'
import { tokenizeLenient, type LenientToken } from './formulaLanguage'
import { findEnclosingCall } from './formulaSignature'
import { GLOBAL_FORMAT_COMPLETIONS } from '@/shared/formula/globalFormats'

// ── The schema the editor completes against ───────────────────────────────────────────────────

export interface FormulaCompletionColumn {
  field: string
  name: string
  /** Shown as the entry's `detail`. Absent for a column whose type is not known. */
  type?: CellType
}

export interface FormulaCompletionSheet {
  id: string
  name: string
  documentId: string
  columns: readonly FormulaCompletionColumn[]
}

export interface FormulaCompletionDocument {
  id: string
  name: string
}

/**
 * Everything the completion source may look at, all of it synchronous.
 *
 * `documents` being `undefined` means the list has not loaded yet: the source then offers a
 * disabled placeholder and calls `requestDocuments()`, and whoever fulfils it re-opens the
 * dropdown with `startCompletion` — the same shape as the Grid's existing `workbookOverridesReady`
 * gate, which is how lazily-loaded data already reaches the formula layer.
 */
export interface FormulaCompletionSchema {
  currentDocumentId: string
  currentSheetId: string
  /** Sheets of the CURRENT document. */
  sheets: readonly FormulaCompletionSheet[]
  documents?: readonly FormulaCompletionDocument[]
  sheetsOfDocument?: (documentId: string) => readonly FormulaCompletionSheet[] | undefined
  requestDocuments?: () => void
  requestSheets?: (documentId: string) => void
}

// ── Context detection ─────────────────────────────────────────────────────────────────────────

export type CompletionSpot =
  /** Start of a token, or inside a bare word: functions, sheets of this document, documents. */
  | { kind: 'open'; from: number; query: string }
  /** Right after a complete container path with no bracket yet: offer `.` and `[`. */
  | { kind: 'afterContainer'; from: number; path: readonly string[] }
  /** Inside `[...]`, with a container path in front of it (possibly empty). */
  | { kind: 'column'; from: number; query: string; path: readonly string[] }
  /** After `'Doc'.`: only that document's sheets. */
  | { kind: 'sheet'; from: number; query: string; document: string }

/** Tokens that can end a container path. */
const isName = (token: LenientToken | undefined): boolean => token?.kind === 'containerName'

/**
 * What the caret is in the middle of. `null` means "offer nothing" — notably inside a `"..."`
 * text literal, which is the one string form this grammar has (single quotes are always a
 * container name here, never a literal).
 */
export function completionContextAt(
  text: string,
  caret: number,
  locale: FormulaLocale,
): CompletionSpot | null {
  const tokens = tokenizeLenient(text, locale)

  // A token the caret sits strictly inside, or one ending exactly at it.
  const at = tokens.filter((token) => token.start < caret && caret <= token.end)
  const current = at[at.length - 1]

  // Inside a text literal there is nothing to complete.
  if (current?.kind === 'string' && text.charAt(current.start) === '"') return null

  // Inside `[...]`: a column, with whatever container path precedes the bracket.
  const open = text.lastIndexOf('[', caret - 1)
  if (open !== -1 && !text.slice(open + 1, caret).includes(']')) {
    const afterBracket = open + 1
    // `@` is part of the syntax, not of the name being matched.
    const hasAt = text.charAt(afterBracket) === '@'
    const from = hasAt ? afterBracket + 1 : afterBracket
    if (from > caret) return null
    return {
      kind: 'column',
      from,
      query: text.slice(from, caret),
      path: pathBefore(tokens, open),
    }
  }

  // Right after `'Doc'.` — the dot is the last thing typed, so only sheets of that document.
  const beforeCaret = tokens.filter((token) => token.end <= caret)
  const last = beforeCaret[beforeCaret.length - 1]
  if (last?.kind === 'dot' && last.end === caret) {
    const name = beforeCaret[beforeCaret.length - 2]
    if (isName(name)) return { kind: 'sheet', from: caret, query: '', document: name?.name ?? '' }
  }
  // ...or partway through the sheet name that follows it.
  if (isName(current) && current !== undefined) {
    const previous = tokens[tokens.indexOf(current) - 1]
    if (previous?.kind === 'dot') {
      const document = tokens[tokens.indexOf(current) - 2]
      if (isName(document)) {
        return {
          kind: 'sheet',
          from: current.start,
          query: current.name ?? '',
          document: document?.name ?? '',
        }
      }
    }
    // A complete quoted name with the caret just past its closing quote: the user has named a
    // container and now needs either `.` or `[`.
    if (current.quoted === true && current.end === caret) {
      return { kind: 'afterContainer', from: caret, path: [current.name ?? ''] }
    }
    return { kind: 'open', from: current.start, query: current.name ?? '' }
  }

  // Anything else: a fresh token position, but only where an operand may start.
  if (current === undefined || current.kind === 'operator' || current.kind === 'open' || current.kind === 'argSep') {
    return { kind: 'open', from: caret, query: '' }
  }
  return null
}

/** The container names written immediately before position `end`, outermost first. */
function pathBefore(tokens: readonly LenientToken[], end: number): string[] {
  const before = tokens.filter((token) => token.end <= end)
  const path: string[] = []
  let index = before.length - 1
  // Walk back over `name (dot name)*`, and tolerate the stray dot the parser also forgives.
  if (before[index]?.kind === 'dot') index--
  while (index >= 0 && isName(before[index])) {
    path.unshift(before[index]?.name ?? '')
    if (before[index - 1]?.kind !== 'dot') break
    index -= 2
  }
  return path
}

// ── Keyword arguments ─────────────────────────────────────────────────────────────────────────

export interface KeywordSpot {
  /** Replaced range: a bare word being typed, or the whole `"..."` literal the caret is inside. */
  from: number
  to: number
  query: string
  words: readonly string[]
}

/**
 * The caret sits where a function takes one of a few words (`KEYWORD_ARGUMENTS`, e.g. COST's
 * `"Unit"`/`"Total"`): at the start of that argument, partway through a bare word, or inside its
 * `"..."`. Just past a closing quote is not a spot: the word is chosen, and reopening the list
 * there would trap the caret.
 */
export function keywordSpotAt(
  text: string,
  caret: number,
  locale: FormulaLocale,
  functionMetadata: readonly FunctionMetadata[],
): KeywordSpot | null {
  const call = findEnclosingCall(text, caret, locale, functionMetadata)
  const words = call === null ? undefined : KEYWORD_ARGUMENTS[call.funcName]?.[call.activeArgIndex]
  if (words === undefined) return null

  const tokens = tokenizeLenient(text, locale)
  const at = tokens.filter((token) => token.start < caret && caret <= token.end)
  const current = at[at.length - 1]
  if (current?.kind === 'string' && text.charAt(current.start) === '"') {
    const closed = current.end - current.start >= 2 && text.charAt(current.end - 1) === '"'
    if (closed && caret === current.end) return null
    return { from: current.start, to: current.end, query: text.slice(current.start + 1, caret), words }
  }
  const spot = completionContextAt(text, caret, locale)
  if (spot?.kind !== 'open') return null
  return { from: spot.from, to: caret, query: spot.query, words }
}

/** The words of a keyword spot as completions, each inserted quoted over the whole spot. */
function keywordOptions(spot: KeywordSpot): Completion[] {
  const options: Completion[] = []
  // Prefix, not fuzzy: among a handful of fixed words, "T" means Total, not the t in Unit.
  const query = spot.query.trim().toLowerCase()
  for (const [index, word] of spot.words.entries()) {
    if (!word.toLowerCase().startsWith(query)) continue
    options.push({
      label: word,
      type: 'constant',
      boost: 50 - index,
      apply: (view) => {
        view.dispatch({
          changes: { from: spot.from, to: spot.to, insert: `"${word}"` },
          selection: { anchor: spot.from + word.length + 2 },
        })
      },
    })
  }
  return options
}

// ── Legacy helpers, still used by FormulaEditor ───────────────────────────────────────────────

/**
 * The reference being typed at the caret: the text between an unclosed `[` and the caret.
 * `null` when the caret is not inside a bracket.
 */
export function refBeingTyped(text: string, caret: number): { start: number; query: string } | null {
  const open = text.lastIndexOf('[', caret - 1)
  if (open === -1) return null
  const between = text.slice(open + 1, caret)
  if (between.includes(']')) return null
  return { start: open, query: between }
}

/**
 * The function name being typed at the caret, if any. `null` when the caret is inside a column
 * reference or not at a position where a function may start.
 */
export function funcBeingTyped(text: string, caret: number): { start: number; query: string } | null {
  const open = text.lastIndexOf('[', caret - 1)
  if (open !== -1) {
    const between = text.slice(open + 1, caret)
    if (!between.includes(']')) return null
  }

  let start = caret
  while (start > 0 && /[\p{L}\p{N}_]/u.test(text.charAt(start - 1))) {
    start--
  }

  if (start > 0) {
    const prevChar = text.charAt(start - 1)
    if (/[\p{L}\p{N}_\]]/u.test(prevChar)) return null
  }

  if (caret < text.length && text.charAt(caret) === '(') return null

  const query = text.slice(start, caret)
  if (query.length === 0) {
    if (start > 0 && (text.charAt(start - 1) === '=' || text.charAt(start - 1) === '(')) {
      return { start, query: '' }
    }
    return null
  }
  return { start, query }
}

// ── Matching and ranking ──────────────────────────────────────────────────────────────────────

/**
 * Accent- and case-insensitive subsequence match, so "hoja1" finds "Hoja 1" and "ventas" finds
 * "Ventás". Returns a score — lower is better — or `null` for no match. A prefix match beats a
 * scattered one, which is what keeps the obvious candidate at the top.
 */
export function fuzzyScore(query: string, candidate: string): number | null {
  const q = fold(query.trim())
  const c = fold(candidate)
  if (q === '') return 0
  if (c.startsWith(q)) return 0
  const contains = c.indexOf(q)
  if (contains !== -1) return 1 + contains

  // Subsequence: every character of the query in order, spaces in the candidate skipped freely.
  let index = 0
  let gaps = 0
  for (const char of q) {
    const found = c.indexOf(char, index)
    if (found === -1) return null
    gaps += found - index
    index = found + 1
  }
  return 100 + gaps
}

// ── Insertion ─────────────────────────────────────────────────────────────────────────────────

/**
 * Folding for MATCHING, which is laxer than the parser's: accents are ignored here (typing
 * "cafe" should find "Café" in a dropdown, even though the two are different columns to the
 * parser) and so is the emoji variation selector, so `📊` finds `📊️`.
 *
 * NFC first, so a decomposed paste folds the same way — otherwise stripping combining marks would
 * turn "Café" into "Cafe" in one form and leave it alone in the other.
 */
const fold = (text: string): string => normalizeGridSearchText(text.normalize('NFC')).replace(/\uFE0F/g, '')

/** A container name written the way the serializer would write it: quoted only when it must be. */
export function quoteName(name: string): string {
  return containerNameNeedsQuotes(name) ? `'${name.replace(/'/g, "''")}'` : name
}

/**
 * One insertion, aware of what surrounds it.
 *
 * `trimDotBefore` is the fix the brief asks for in two places at once: when the text before the
 * insertion point ends in `.` and what is being inserted starts with `[`, that one dot goes. Only
 * that one — the dot separating a document from a sheet is further left and is never touched.
 */
function applyInsert(
  insert: string,
  options: { reopen?: boolean; caretOffset?: number } = {},
): (view: EditorView, completion: Completion, from: number, to: number) => void {
  return (view, _completion, from, to) => {
    const doc = view.state.doc.toString()

    let start = from
    let text = insert

    // Case 1: we are inserting the bracket ourselves, straight after a dot.
    if (text.startsWith('[') && doc.slice(0, start).endsWith('.')) {
      start -= 1
    } else {
      // Case 2: the bracket is already there and we are filling in the column. The dot to drop
      // then sits before that bracket, so the replaced range is widened to swallow it and the
      // text in between (`[`, and `@` if the user typed one) is carried along. Only the dot
      // touching the bracket qualifies — a document separator is further left, behind a name.
      let bracket = start
      while (bracket > 0 && (doc.charAt(bracket - 1) === '@')) bracket--
      if (doc.charAt(bracket - 1) === '[' && doc.charAt(bracket - 2) === '.') {
        text = doc.slice(bracket - 1, start) + text
        start = bracket - 2
      }
    }

    // Never double a character the user already typed just after the caret. `closeBrackets()`
    // leaves a `]` behind, and a determined typist supplies their own quote or dot.
    let end = to
    for (const char of ['[', ']', '.', "'"]) {
      if (text.endsWith(char) && doc.charAt(end) === char) {
        text = text.slice(0, -1)
        break
      }
    }
    if (doc.charAt(end) === ']' && text.endsWith(']')) {
      end += 1
    }

    const anchor = start + text.length + (options.caretOffset ?? 0)
    view.dispatch({ changes: { from: start, to: end, insert: text }, selection: { anchor } })
    // A document leads to its sheets, a sheet to its columns: keep the chain going rather than
    // making the user re-open the dropdown at every level.
    if (options.reopen) startCompletion(view)
  }
}

// ── The completion source ─────────────────────────────────────────────────────────────────────

const COLUMN_VALID = /^[^\][]*$/
const NAME_VALID = /^[\p{L}\p{N}_ ]*$/u

/**
 * Offers functions, documents, sheets and columns according to where the caret is.
 *
 * Filtering and ordering are done here (`filter: false`), because the ranking is about proximity —
 * the current sheet first, then the rest of this document, then other documents — and CM6's own
 * scoring knows nothing about that.
 */
export interface FormulaOperandRef {
  /** Column name, matched against what is typed. */
  name: string
  /** Text inserted, e.g. `[Price]` or `[Price]12`. */
  ref: string
}

export function formulaCompletionSource(
  schema: FormulaCompletionSchema,
  functionMetadata: readonly FunctionMetadata[],
  locale: FormulaLocale,
  /** Ready-made references offered first where an operand goes: the other columns of this sheet
   *  (`[@Col]` in a column formula) or the other cells of this row (`[Col]12` in a cell formula). */
  operandRefs: () => readonly FormulaOperandRef[] = () => [],
): CompletionSource {
  const currentSheet = (): FormulaCompletionSheet | undefined =>
    schema.sheets.find((sheet) => sheet.id === schema.currentSheetId)

  /** The sheet a container path points at, or the current one when the path is empty. */
  const resolveSheet = (path: readonly string[]): FormulaCompletionSheet | undefined => {
    if (path.length === 0) return currentSheet()
    // Exact lookups, unlike the fuzzy matching above, must agree with the parser exactly —
    // otherwise the dropdown resolves a path the parser then rejects.
    const match = (sheets: readonly FormulaCompletionSheet[], name: string) =>
      sheets.find((sheet) => normaliseContainerName(sheet.name) === normaliseContainerName(name))
    if (path.length === 1) return match(schema.sheets, path[0] ?? '')
    const document = schema.documents?.find(
      (d) => normaliseContainerName(d.name) === normaliseContainerName(path[0] ?? ''),
    )
    if (document === undefined) return undefined
    return match(schema.sheetsOfDocument?.(document.id) ?? [], path[1] ?? '')
  }

  const columnOptions = (
    sheet: FormulaCompletionSheet | undefined,
    query: string,
    showSheet: boolean,
  ): Completion[] => {
    if (sheet === undefined) return []
    const options: Completion[] = []
    for (const column of sheet.columns) {
      const score = fuzzyScore(query, column.name)
      if (score === null) continue
      const detailText = [column.type, showSheet ? sheet.name : undefined].filter(Boolean).join(' · ')
      const detail = detailText === '' ? {} : { detail: detailText }
      // Twice, deliberately: the whole column and the current row are different references, and
      // the brief asks for both to be visible rather than hidden behind typing `@`.
      options.push({
        label: column.name,
        ...detail,
        type: 'column',
        boost: 1 - score / 1000,
        apply: applyInsert(`${escapeColumn(column.name)}]`),
      })
      options.push({
        label: `@${column.name}`,
        ...detail,
        type: 'column',
        boost: -1 - score / 1000,
        apply: applyInsert(`@${escapeColumn(column.name)}]`),
      })
    }
    return options
  }

  return (context: CompletionContext): CompletionResult | null => {
    const text = context.state.doc.toString()

    // An argument that takes one of a few words gets only those words, and gets them at once:
    // there is nothing else that makes sense to type there, and nothing to discover by typing.
    const keyword = keywordSpotAt(text, context.pos, locale, functionMetadata)
    if (keyword !== null) {
      const options = keywordOptions(keyword)
      return options.length === 0 ? null : { from: keyword.from, to: keyword.to, options, filter: false }
    }

    const spot = completionContextAt(text, context.pos, locale)
    if (spot === null) return null

    if (spot.kind === 'column') {
      // The `@` the user already typed means only the current-row variants make sense.
      const typedAt = text.charAt(spot.from - 1) === '@'
      const sheet = resolveSheet(spot.path)
      const all = columnOptions(sheet, spot.query, sheet?.id !== schema.currentSheetId)
      const options = typedAt
        ? all.filter((option) => option.label.startsWith('@')).map((option) => ({
          ...option,
          label: option.label.slice(1),
          apply: applyInsert(`${escapeColumn(option.label.slice(1))}]`),
        }))
        : all
      return { from: spot.from, options, validFor: COLUMN_VALID }
    }

    if (spot.kind === 'sheet') {
      const document = schema.documents?.find(
        (d) => normaliseContainerName(d.name) === normaliseContainerName(spot.document),
      )
      const sheets = document === undefined
        ? schema.sheets
        : schema.sheetsOfDocument?.(document.id)
      if (sheets === undefined) {
        if (document !== undefined) schema.requestSheets?.(document.id)
        return { from: spot.from, options: [loadingOption()], validFor: NAME_VALID }
      }
      return {
        from: spot.from,
        options: sheetOptions(sheets, spot.query, schema.currentSheetId),
        validFor: NAME_VALID,
      }
    }

    if (spot.kind === 'afterContainer') {
      // Both ways forward from a named container, spelled out rather than guessed at.
      return {
        from: spot.from,
        options: [
          { label: '[', detail: 'columna', type: 'column', apply: applyInsert('[') },
          { label: '.', detail: 'hoja', type: 'sheet', apply: applyInsert('.', { reopen: true }) },
        ],
        validFor: /^$/,
      }
    }

    // `open` with nothing typed yet (just after `=`, an operator, `(` or a separator): the list
    // waits for a letter or `[`, so it doesn't cover the formula while the user types operators.
    // Ctrl+Space still asks for it explicitly.
    if (spot.query === '' && !context.explicit) return null

    // `open`: functions, this document's sheets, and other documents (or global formats if in TEXT).
    const options: Completion[] = []

    const enclosing = findEnclosingCall(text, context.pos, locale, functionMetadata)
    const isTextFormatArg = enclosing?.funcName === 'TEXT' && enclosing.activeArgIndex === 1

    if (isTextFormatArg) {
      for (const fmt of GLOBAL_FORMAT_COMPLETIONS) {
        const score = fuzzyScore(spot.query, fmt.label)
        if (score === null) continue
        options.push({
          label: fmt.label,
          detail: fmt.pattern,
          type: 'constant',
          boost: 10 - score / 1000,
          apply: applyInsert(fmt.identifier),
        })
      }
      if (fuzzyScore(spot.query, '""') !== null || spot.query === '') {
        options.push({
          label: '""',
          detail: 'Formato personalizado',
          type: 'text',
          boost: 8,
          apply: (view, _completion, from, to) => {
            const doc = view.state.doc.toString()
            let start = from
            let end = to
            if (doc.charAt(end) === '"' && doc.charAt(end + 1) === '"') {
              end += 2
            }
            view.dispatch({
              changes: { from: start, to: end, insert: '""' },
              selection: { anchor: start + 1 },
            })
          },
        })
      }
    }

    if (!isTextFormatArg) {
      for (const [index, operand] of operandRefs().entries()) {
        const score = fuzzyScore(spot.query, operand.name)
        if (score === null) continue
        options.push({
          label: operand.ref,
          type: 'variable',
          // First, in the sheet's column order: the neighbouring values are what a formula most
          // often combines. (The boost range is -99..99; the index keeps ties out of label order.)
          boost: Math.max(11, 20 - index * 0.05 - score / 1000),
          apply: applyInsert(operand.ref),
        })
      }
    }

    for (const fn of functionMetadata) {
      const score = fuzzyScore(spot.query, fn.name)
      if (score === null) continue
      options.push({
        label: fn.name,
        detail: fn.syntax,
        info: fn.description,
        type: 'function',
        boost: (isTextFormatArg ? -5 : 2) - score / 1000,
        apply: applyInsert(`${fn.name}(`),
      })
    }
    options.push(...sheetOptions(schema.sheets, spot.query, schema.currentSheetId))

    // Documents are only offered where something actually provides them. Nothing does yet — a
    // reference into another document parses and serializes but evaluates to `#¡REF!` until the
    // cross-document loading pipeline exists, so offering one here would only invite a formula
    // that cannot compute. The placeholder is for a provider that is still loading, not for the
    // absence of one.
    if (schema.documents === undefined && schema.requestDocuments !== undefined) {
      schema.requestDocuments()
      options.push(loadingOption())
    } else if (schema.documents !== undefined) {
      for (const document of schema.documents) {
        if (document.id === schema.currentDocumentId) continue
        const score = fuzzyScore(spot.query, document.name)
        if (score === null) continue
        options.push({
          label: document.name,
          type: 'document',
          // Last: another document is the furthest thing from the cell being edited.
          boost: -3 - score / 1000,
          apply: applyInsert(`${quoteName(document.name)}.`, { reopen: true }),
        })
      }
    }

    return { from: spot.from, options, validFor: NAME_VALID }
  }
}

/** Sheets of one document, ranked with the current sheet first. */
function sheetOptions(
  sheets: readonly FormulaCompletionSheet[],
  query: string,
  currentSheetId: string,
): Completion[] {
  const options: Completion[] = []
  for (const sheet of sheets) {
    // A grid with no workbook snapshot still reports a current sheet, but with no name — there is
    // nothing to offer for it, and an empty query matches everything, so it would show as a blank
    // row in the dropdown.
    if (sheet.name === '') continue
    const score = fuzzyScore(query, sheet.name)
    if (score === null) continue
    options.push({
      label: sheet.name,
      type: 'sheet',
      boost: (sheet.id === currentSheetId ? 3 : 0) - score / 1000,
      apply: applyInsert(`${quoteName(sheet.name)}[`, { reopen: true }),
    })
  }
  return options
}

/** A non-insertable placeholder shown while a schema lookup is still in flight. */
const loadingOption = (): Completion => ({
  label: '…',
  type: 'document',
  boost: -99,
  apply: () => {},
})

/** `]` inside a column name is doubled, exactly as `serialize` writes it; header line breaks
 *  become spaces so the reference stays on one line. */
const escapeColumn = (name: string): string => name.replace(/\s*\n\s*/g, ' ').trim().replace(/]/g, ']]')
